<template>
  <div class="custom-ion-select" :class="'size-' + size">
    <!-- Pill variant carries its own label inside the trigger, so the stacked label is skipped. -->
    <CustomIonLabel
      v-if="inputLabel && !isPill"
      :inputLabel="inputLabel"
      :inputId="inputId"
      :isMandatory="isMandatory"
      :size="size"
    />
    <!-- Adaptive single option → auto-filled, read-only (no picker to open). -->
    <div
      v-if="isSingleAutoFill"
      class="control-box color-text row-center-gap-2 w-full px-3 bg-color-light select-readonly"
    >
      <span class="flex-1 text-left truncate leading-m min-w-0">{{ singleName }}</span>
      <i class="ti ti-check color-medium text-xs shrink-0" aria-hidden="true"></i>
    </div>
    <!-- Adaptive small list (<= inlineThreshold) → inline radio pills instead of a picker. -->
    <CustomIonRadioButton
      v-else-if="isInline"
      :inputId="inputId"
      :radioItems="dropdownItems"
      :initialValue="radioInitialValue"
      :isDisabled="isDisabled"
      :size="size"
      @onChange="onInlineChange"
    />
    <CustomPillTrigger
      v-else-if="isPill"
      :triggerId="triggerId"
      :triggerLabel="inputLabel"
      :triggerText="displayText || placeholder"
      :triggerIcon="triggerIcon"
      :count="pillCount"
      :color="pillColor"
      :size="size"
      :isDisabled="isDisabled"
      :isExpanded="isModalOpen"
      :isClearable="isClearable && hasValue"
      :isPlaceholder="!displayText"
      :ariaLabel="inputLabel"
      @onClick="openModal"
      @onClear="clearValue"
    />
    <button
      v-else
      type="button"
      :id="triggerId"
      :disabled="isDisabled"
      class="control-box row-center-gap-2 w-full px-3"
      :class="[
        isDisabled
          ? 'cursor-not-allowed bg-color-medium-light color-medium'
          : 'cursor-pointer bg-color-transparent color-text',
      ]"
      @click="openModal"
    >
      <span
        class="flex-1 text-left truncate leading-m min-w-0"
        :class="{ 'color-medium': !displayText }"
      >
        {{ displayText || placeholder }}
      </span>
      <IonIcon
        v-if="isClearable && hasValue"
        :icon="closeOutline"
        class="ion-select-clear color-medium text-m shrink-0"
        @click.stop="clearValue"
      />
      <IonIcon :icon="chevronDownOutline" class="color-medium text-xs shrink-0" />
    </button>
    <IonModal
      ref="modalRef"
      v-if="!isInline && dropdownType === DropdownTypeEnum.MODAL"
      :isOpen="isModalOpen"
      @didPresent="focusSearch"
      @willDismiss="isModalOpen = false"
      @ionModalDidDismiss="isModalOpen = false"
      class="ion-select-modal"
    >
      <CustomIonSelectContent
        ref="contentRef"
        :inputId="inputId"
        :inputLabel="inputLabel"
        :dropdownItems="dropdownItems"
        :initialValue="inputValue ? inputValue : undefined"
        :inputPlaceholder="placeholder"
        :isMultiSelect="isMultiSelect"
        :isFilterable="isSearchEnabled"
        :searchField="searchField"
        :isDisabled="isDisabled"
        :size="size"
        @onChangeSelectedValue="onChangeSelectedValue"
        @onClose="onDismissModal"
        class="ion-select-modal-wrapper"
      />
    </IonModal>
    <IonPopover
      v-else-if="!isInline && dropdownType === DropdownTypeEnum.POPOVER"
      :trigger="!isDisabled ? triggerId : undefined"
      :alignment="popoverAlignment"
      size="cover"
      class="ion-select-popover"
      :arrow="false"
      @didPresent="focusSearch"
    >
      <CustomIonSelectContent
        ref="contentRef"
        :inputId="inputId"
        :inputLabel="inputLabel"
        :dropdownItems="dropdownItems"
        :inputPlaceholder="placeholder"
        :initialValue="inputValue ? inputValue : undefined"
        :isMultiSelect="isMultiSelect"
        :isFilterable="isSearchEnabled"
        :searchField="searchField"
        :isDisabled="isDisabled"
        :size="size"
        @onChangeSelectedValue="onChangeSelectedValue"
        @onClose="onDismissPopover"
        class="ion-select-popover-wrapper"
      />
    </IonPopover>
    <!-- The pill is a toolbar control, not a form-field box: it keeps the hidden Field binding
         but reserves no validation row beneath itself (that would break toolbar alignment). -->
    <div v-if="isFormField" :class="{ 'validation-slot': !isPill }">
      <Field v-model="inputValue" :id="inputId" :name="inputId" hidden />
      <ErrorMessage v-if="!isPill" :name="inputId" class="validation-message" />
    </div>
  </div>
</template>

<script lang="ts">
import CustomPillTrigger from "@/components/atoms/CustomPillTrigger.vue";
import CustomIonLabel from "@/components/inputs/CustomIonLabel.vue";
import CustomIonRadioButton from "@/components/inputs/CustomIonRadioButton.vue";
import CustomIonSelectContent from "@/components/inputs/CustomIonSelectContent.vue";
import { DropdownTypeEnum } from "@/enums/global";
import { getNMoreString } from "@/methods/utils";
import type {
  ComponentSizeType,
  FilterOptionItem,
  PillColorType,
  SelectTriggerVariant,
} from "@/types/componentType";
import { IonIcon, IonModal, IonPopover, popoverController } from "@ionic/vue";
import { checkmarkOutline, chevronDownOutline, closeOutline } from "ionicons/icons";
import { ErrorMessage, Field } from "vee-validate";
import { PropType, computed, defineComponent, onMounted, ref } from "vue";

// Below this many options a search field earns nothing: the whole list already fits without
// scrolling, and `isFilterable` also pins the overlay to `select-fixed-height` (80vh / 50vh),
// leaving a mostly-empty picker. Short lists therefore render at their natural height.
const SEARCH_MIN_ITEMS = 10;

// Autofocusing the search field is a desktop-only affordance. On a touch device it summons the
// software keyboard the moment the picker opens, covering half the option list — worse than the
// one tap it saves. `pointer: coarse` is the signal for "typing here costs a keyboard", and it
// covers the native apps and mobile browsers alike (unlike Capacitor's isNativePlatform).
function hasSoftwareKeyboard(): boolean {
  return window.matchMedia?.("(pointer: coarse)").matches ?? false;
}

export default defineComponent({
  name: "CustomIonSelect",
  components: {
    CustomIonLabel,
    CustomIonRadioButton,
    CustomPillTrigger,
    IonModal,
    IonPopover,
    CustomIonSelectContent,
    ErrorMessage,
    Field,
    IonIcon,
  },
  props: {
    inputId: { type: String, required: true },
    inputLabel: { type: String, required: false },
    // FilterOptionItem (= InputOption + optional Count / Color) so the pill trigger can show the
    // selected option's count; plain InputOption lists stay assignable unchanged.
    dropdownItems: { type: Array as PropType<Array<FilterOptionItem>>, required: true },
    // Declared with an explicit PropType because the runtime constructor list cannot express
    // `null` — which this prop both defaults to and stores directly into `inputValue` below.
    initialValue: {
      type: [Number, Array, String] as PropType<number | string | Array<number | string> | null>,
      default: null,
    },
    inputPlaceholder: { type: String, required: false },
    isMultiSelect: { type: Boolean, default: false },
    isMandatory: { type: Boolean, default: false },
    isFormField: { type: Boolean, default: false },
    isClearable: { type: Boolean, default: false },
    isFilterable: { type: Boolean, default: false },
    searchField: { type: String, default: "value" },
    dropdownType: { type: String, default: DropdownTypeEnum.MODAL },
    popoverAlignment: { type: String, default: "start" },
    isDisabled: { type: Boolean, default: false },
    // Opt-in adaptive layout for single-select: when the option count is 1..inlineThreshold the
    // field renders inline instead of behind a picker — 1 auto-fills read-only, 2..N as radio pills.
    // 0 (default) keeps the classic trigger + overlay for every existing call site.
    inlineThreshold: { type: Number, default: 0 },
    size: { type: String as PropType<ComponentSizeType>, default: "default" },
    // "field" (default) keeps the bordered input-style box; "pill" renders the rounded toolbar
    // trigger used by CustomFilterOptions — label, value, count and chevron inside one pill.
    triggerVariant: { type: String as PropType<SelectTriggerVariant>, default: "field" },
    // Pill fill; null = the neutral grey pill. Ignored by the field variant.
    pillColor: { type: String as PropType<PillColorType | null>, default: null },
    // Tabler icon name (without "ti-") rendered at the pill's leading edge.
    triggerIcon: { type: String, default: null },
  },
  setup(props, { emit }) {
    const inputValue = ref<number | Array<number | string> | string | null>(props.initialValue);
    const isModalOpen = ref(false);
    const modalRef = ref<InstanceType<typeof IonModal> | null>(null);
    const contentRef = ref<InstanceType<typeof CustomIonSelectContent> | null>(null);

    const triggerId = computed(() => "customSelectTrigger-" + props.inputId);
    const isPill = computed(() => props.triggerVariant === "pill");

    // `isFilterable` is a request, not a guarantee — honour it only once the list is long
    // enough to be worth searching (see SEARCH_MIN_ITEMS).
    const isSearchEnabled = computed(
      () => props.isFilterable && props.dropdownItems.length >= SEARCH_MIN_ITEMS,
    );

    const placeholder = computed(() => {
      return props.inputPlaceholder ? props.inputPlaceholder : `Select ${props.inputLabel ?? ""}`;
    });

    const hasValue = computed(() => {
      if (Array.isArray(inputValue.value)) {
        return inputValue.value.length > 0;
      }
      return inputValue.value ? true : false;
    });

    const displayText = computed(() => {
      if (!hasValue.value) {
        return "";
      }
      if (props.isMultiSelect && Array.isArray(inputValue.value)) {
        const selectedItemsNames = props.dropdownItems
          .filter((item) => (inputValue.value as Array<number | string>).includes(item.Id))
          .map((item) => item.Name);
        return selectedItemsNames.length ? getNMoreString(selectedItemsNames) : "";
      }
      const selectedItem = props.dropdownItems.find((item) => item.Id === inputValue.value);
      return selectedItem ? selectedItem.Name : "";
    });

    // Count suffix on the pill — only meaningful for a single selection, where exactly one
    // option's Count is on display.
    const pillCount = computed<number | null>(() => {
      if (props.isMultiSelect || !hasValue.value) {
        return null;
      }
      const selectedItem = props.dropdownItems.find((item) => item.Id === inputValue.value);
      return selectedItem?.Count ?? null;
    });

    // Adaptive layout (opt-in via inlineThreshold): a small single-select renders inline instead of
    // behind a picker — 1 option auto-fills read-only, 2..inlineThreshold render as radio pills.
    // The pill opts out: a toolbar pill has no room for a radio-pill row, and an auto-filled
    // read-only box is a form-field shape that would break the toolbar it sits in.
    const isInline = computed(
      () =>
        !isPill.value &&
        !props.isMultiSelect &&
        props.inlineThreshold > 0 &&
        props.dropdownItems.length >= 1 &&
        props.dropdownItems.length <= props.inlineThreshold,
    );
    const isSingleAutoFill = computed(() => isInline.value && props.dropdownItems.length === 1);
    const singleName = computed(() => props.dropdownItems[0]?.Name ?? "");
    const radioInitialValue = computed(() =>
      inputValue.value != null && !Array.isArray(inputValue.value) ? inputValue.value : undefined,
    );

    // Single option → pre-select it so the field submits without any interaction, then announce it
    // once mounted so the parent's model + vee-validate field are populated.
    if (isSingleAutoFill.value) {
      inputValue.value = props.dropdownItems[0].Id;
    }
    onMounted(() => {
      if (isSingleAutoFill.value) {
        emit("onChange", props.dropdownItems[0].Id);
      }
    });

    function onInlineChange(value: number | string) {
      inputValue.value = value;
      emit("onChange", value);
    }

    function openModal() {
      if (props.isDisabled) {
        return;
      }
      isModalOpen.value = true;
    }

    function onDismissModal() {
      isModalOpen.value = false;
    }

    async function onDismissPopover() {
      await popoverController.dismiss();
    }

    async function onChangeSelectedValue(selectedValue: number | string | Array<number | string>) {
      inputValue.value = selectedValue;
      if (props.dropdownType === DropdownTypeEnum.POPOVER) {
        emit("onChange", inputValue.value);
        await onDismissPopover();
      } else if (modalRef.value) {
        emit("onChange", inputValue.value);
        await (modalRef.value as any).$el.dismiss();
        isModalOpen.value = false;
      }
    }

    // Ionic emits `didPresent` before it moves focus to the overlay container; focusing the
    // searchbar here lands inside the overlay, so Ionic's own focus call is skipped.
    async function focusSearch(): Promise<void> {
      if (hasSoftwareKeyboard()) {
        return;
      }
      await contentRef.value?.focusSearch();
    }

    function clearValue() {
      inputValue.value = props.isMultiSelect ? [] : null;
      emit("onChange", inputValue.value);
    }

    return {
      inputValue,
      isModalOpen,
      modalRef,
      contentRef,
      isSearchEnabled,
      triggerId,
      placeholder,
      hasValue,
      displayText,
      isPill,
      pillCount,
      isInline,
      isSingleAutoFill,
      singleName,
      radioInitialValue,
      onInlineChange,
      checkmarkOutline,
      chevronDownOutline,
      closeOutline,
      DropdownTypeEnum,
      openModal,
      onDismissModal,
      onDismissPopover,
      onChangeSelectedValue,
      focusSearch,
      clearValue,
    };
  },
});
</script>

<style lang="scss" scoped>
.ion-select-clear {
  &:hover {
    color: var(--color-danger);
  }
}
</style>

<style lang="scss">
.ion-select-modal {
  // Mobile-first: near-full-width by default, capped so a desktop viewport doesn't stretch a
  // list of short option labels across the screen. Matches `.tree-select-modal`.
  --width: 95%;
  --max-width: 25rem;
  --height: auto;
  --max-height: 80%;
  --border-radius: var(--radius-2xl);
  @include media(">=xs") {
    --width: 90%;
  }
}
.ion-select-popover {
  --max-height: 50vh;
  --border-radius: 10px;
}
.ion-select-modal-wrapper {
  max-height: 80vh;
}
.ion-select-popover-wrapper {
  max-height: 50vh;
}
.ion-select-modal-wrapper.select-fixed-height {
  height: 80vh;
}
.ion-select-popover-wrapper.select-fixed-height {
  height: 50vh;
}
.ion-select-modal-wrapper,
.ion-select-popover-wrapper {
  & .custom-ion-radio,
  & .custom-ion-checkbox {
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding-inline: 0.5rem;
  }
  & ion-searchbar.custom-ion-searchbar,
  & .custom-ion-checkbox > .row-center-gap-2 {
    flex-shrink: 0;
  }
  & .custom-ion-radio ion-radio-group,
  & .custom-ion-checkbox > .flex.flex-col {
    min-height: 0;
    overflow-y: auto;
    scrollbar-width: thin !important;
    scrollbar-color: var(--color-medium-transparent) transparent;
    margin-inline: -0.5rem;
    padding-inline: 0.5rem;
    padding-bottom: 0.25rem;
  }
}

/* Trigger button height + font resolve from the `.size-*` switch class on the
   root — one rule. CSS (not Tailwind h-*) avoids Ionic global button interference. */
.custom-ion-select button {
  height: var(--control-height);
  font-size: var(--control-font);
}

/* Read-only box (adaptive single option) matches the trigger height/typography. */
.custom-ion-select .select-readonly {
  min-height: var(--control-height);
  font-size: var(--control-font);
}
</style>
